import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { SHOT_THUMBNAIL_TYPES } from '../../../stores/types/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import Heading from '../../general-components/v2/heading';
import ButtonClose from '../../general-components/button-close';
import Socket from '../../../connection/socket';
import useShots from '../../../contexts/shots/provider';
import { useOpenShotModal } from '../../../hooks/use-shot-utilities';
import useUserActions from '../../../hooks/use-user-actions';
import * as ShotsActions from '../../../stores/actions/shots';
import { ROOM_STATES } from '../../../stores/reducers/settings';
import { sanitizeContentEditablePaste } from '../../../utils/contenteditable-paste';
import ShotThumbnail from '../../general-components/shot-thumbnail';
import ShotDrawerVideoThumbs from './video-thumbs';
import OverlayCards from './overlay-cards';
import BottomButtons from './bottom-buttons';
import ShotTitleDuration from './shot-title-duration';

interface ShotDrawerProps {
	active: boolean;
	wasActive: boolean;
	closeTab: Function;
	socket: Socket | null;
	commsActive: boolean;
	commsDisabled: boolean;
	backstageCommsToggle: (muted: boolean) => void;
}

export default function ShotDrawer(props: ShotDrawerProps) {
	const {
		active,
		wasActive,
		closeTab,
		socket,
		commsActive,
		commsDisabled,
		backstageCommsToggle,
	} = props;

	const shots = useSelector((state: GS) => state.ShotsReducer.shots);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shotTabShotId = useSelector((s: GS) => s.ShotsReducer.shotTabShotId);
	const shotStartTimestampEntity = useSelector(
		(state: GS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const useNewShotThumbs = useSelector((state: GS) => state.SettingsReducer.featureFlags.use_new_shot_thumbs);

	const dispatch = useDispatch();

	const { currentShot, handleSaveOnEnter } = useShots();

	const shotTabShot = shots.find((shot) => shot.shot_id === shotTabShotId) ?? shots[0];

	const isCurrentActiveShot = currentShotId === shotTabShotId;

	const handleOpenShotModal = useOpenShotModal();

	const {
		shot_id: shotId,
		shot_data: shotData,
	} = shotTabShot;

	const {
		cue,
		note,
	} = shotData;

	const [cueInputValue, setCueInputValue] = useState('');
	const [noteInputValue, setNoteInputValue] = useState('');
	const noteRef = useRef<HTMLDivElement | null>(null);

	const openEditShotModal = () => {
		handleOpenShotModal(shotTabShot, socket);
	};

	useEffect(() => {
		if (shotId !== 0) {
			setCueInputValue(cue);
			setNoteInputValue(note);

			if (noteRef.current) {
				noteRef.current.innerText = note;
			}
		}
	}, [shotId, currentShot, dispatch]);

	useEffect(() => {
		// if the shotTabShotId is just the base shot, set it to the current shot
		if (shotId === 0) {
			dispatch(ShotsActions.setShotTabShotId(currentShot.shot_id));
		}
	}, []);

	const applyInputValuesAndSaveShot = () => {
		if (!token) return;

		const newCurrentShot = JSON.parse(JSON.stringify(shotTabShot));

		newCurrentShot.shot_data.cue = cueInputValue;
		newCurrentShot.shot_data.note = noteInputValue;

		dispatch(ShotsActions.editShot(token, newCurrentShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
	};

	const handleCueInputValue = (e: any) => {
		setCueInputValue(e.target.value);
	};

	const shotTabHeaderStatus = useMemo((): JSX.Element => {
		if (roomState === ROOM_STATES.SHOWTIME && isCurrentActiveShot) {
			return (
				<div className="header-status">
					<div className="badge-green border-radius-round bl-font-l3 no-select">
						Live
					</div>
				</div>
			);
		}

		return <></>;
	}, [roomState, isCurrentActiveShot]);

	return (
		<div className={classNames('tab', { active, wasActive })}>
			<Heading
				title={`Shot ${shotTabShot.shot_data.order + 1} of ${shots.length}`}
				badge={shotTabHeaderStatus}
				classNamesExtra='margin-bottom-none'
			/>
			<div className="settings-shots-scroller-glow"/>
			<div className="shots-tab-wrapper">
				<ShotTitleDuration
					shotTabShot={shotTabShot}
					socket={socket}
				/>

				{!isCurrentActiveShot ? (
					<div className="shot-tab-shot-thumbnail-container shot-drawer-improvements">
						<ShotThumbnail
							key={`${shotId}-${shotStartTimestampEntity}`}
							shot={shotTabShot}
							selected={shotId === currentShotId}
							playStart={shotStartTimestampEntity}
							borderRatio={0.5}
							onSelect={openEditShotModal}
							shotThumbnailType={SHOT_THUMBNAIL_TYPES.SHOTS_TAB}
						/>
					</div>
				) : (
					<></>
				)}

				<label className="shots-label">Cue</label>
				<div className="margin-bottom-medium margin-top-small">
					<input
						name="cue"
						className="shot-cue"
						onChange={(e) => handleCueInputValue(e)}
						onBlur={applyInputValuesAndSaveShot}
						onKeyUp={handleSaveOnEnter}
						value={cueInputValue}
						maxLength={50}
						style={{ width: '100%' }}
						placeholder={'What prompts this shot to change?'}
					/>
				</div>

				<OverlayCards
					shotTabShot={shotTabShot}
					socket={socket}
				/>

				<ShotDrawerVideoThumbs
					shotId={shotId}
					shotData={shotData}
					socket={socket}
				/>

				<label className="shots-label">Notes</label>
				<div
					ref={noteRef}
					className="margin-top-small shot-note-container"
					contentEditable
					onInput={() => setNoteInputValue(noteRef.current?.innerText || '')}
					onPaste={sanitizeContentEditablePaste}
					onBlur={applyInputValuesAndSaveShot}
					placeholder-text="Additional information related to the shot"
				></div>

				<BottomButtons
					shotTabShot={shotTabShot}
					socket={socket}
					commsActive={commsActive}
					commsDisabled={commsDisabled}
					backstageCommsToggle={backstageCommsToggle}
					isCurrentActiveShot={isCurrentActiveShot}
					openEditShotModal={openEditShotModal}
				/>
			</div>
		</div>
	);
}
